import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * DashboardTab - Executive Command Center
 * Clean table-based layout for pending items
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaDollarSign, FaMoneyBillWave, FaChartLine, FaCheckCircle, FaSync, FaCheck, FaTimes, FaEnvelope, FaTruck, FaBoxOpen, FaFileAlt, FaExclamationTriangle } from 'react-icons/fa';
import mockDataService from '../../../../services/mockDataService';
import { apiClient } from '../../../../utils/apiClient';
import { notify } from '../../../../utils/notifications';
import { DashboardStats } from './DashboardStats';
import { Button, TableContainer, Table, Th, Td, Tr } from '../SharedComponents';
import { RecordPaymentModal } from '../../../../components/RecordPaymentModal';
import { MemoApprovalModal } from '../../../../components/MemoApprovalModal';
const Container = styled.div `
  padding: 0;
`;
const HeaderActions = styled.div `
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;
const Section = styled.div `
  margin-bottom: 2rem;
`;
const SectionHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${props => props.$color || 'var(--border-color)'};
`;
const SectionTitle = styled.h3 `
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const SectionBadge = styled.span `
  background: ${props => props.$color || '#6b7280'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
`;
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => {
    switch (props.$variant) {
        case 'primary':
            return `
          background: rgba(184, 134, 11, 0.1);
          border-color: #B8860B;
          color: #B8860B;
          &:hover { background: rgba(184, 134, 11, 0.2); }
        `;
        case 'success':
            return `
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
          color: #10b981;
          &:hover { background: rgba(16, 185, 129, 0.2); }
        `;
        case 'danger':
            return `
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
          &:hover { background: rgba(239, 68, 68, 0.2); }
        `;
        default:
            return `
          background: var(--bg-tertiary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover { background: var(--bg-secondary); }
        `;
    }
}}
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: ${props => props.$warning ? '#ef4444' : 'var(--accent-gold)'};
`;
const OverdueBadge = styled.span `
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.$days > 60)
        return '#dc262615';
    if (props.$days > 30)
        return '#f59e0b15';
    return '#10b98115';
}};
  color: ${props => {
    if (props.$days > 60)
        return '#dc2626';
    if (props.$days > 30)
        return '#f59e0b';
    return '#10b981';
}};
`;
const AllCaughtUp = styled.div `
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-tertiary);
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);

  svg {
    font-size: 3rem;
    color: #10b981;
    margin-bottom: 1rem;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
export const DashboardTab = ({ onNavigateToTab }) => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        totalAR: 0,
        totalAP: 0,
        netCash: 0
    });
    // Data lists
    const [unpaidPurchases, setUnpaidPurchases] = useState([]);
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [pendingMemos, setPendingMemos] = useState([]);
    const [approvedMemos, setApprovedMemos] = useState([]);
    const [overdueMemos, setOverdueMemos] = useState([]);
    const [draftInvoices, setDraftInvoices] = useState([]);
    // Modal states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showMemoModal, setShowMemoModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [selectedMemo, setSelectedMemo] = useState(null);
    const fetchDashboard = async () => {
        setLoading(true);
        try {
            if (mockDataService.isMockMode()) {
                const purchases = mockDataService.getPurchases();
                const invoices = mockDataService.getInvoices();
                const memos = mockDataService.getMemos();
                const unpaid = purchases.filter((p) => p.paymentStatus === 'unpaid' || p.paymentStatus === 'partial');
                const outstanding = invoices.filter((inv) => inv.status === 'issued' || inv.status === 'overdue');
                const pending = memos.filter((m) => m.status === 'pending');
                const approved = memos.filter((m) => m.status === 'approved');
                const drafts = invoices.filter((inv) => inv.status === 'draft');
                // Add days overdue calculation
                const now = new Date();
                const unpaidWithDays = unpaid.map((p) => ({
                    ...p,
                    daysOverdue: p.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(p.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).sort((a, b) => b.daysOverdue - a.daysOverdue);
                const outstandingWithDays = outstanding.map((inv) => ({
                    ...inv,
                    daysOverdue: inv.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(inv.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).sort((a, b) => b.daysOverdue - a.daysOverdue);
                // Active memos (shipped or with_client) that are overdue for return
                const activeMemos = memos.filter((m) => m.status === 'shipped' || m.status === 'with_client');
                const overdueActiveMemos = activeMemos.map((m) => ({
                    ...m,
                    daysOverdue: m.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(m.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).filter((m) => m.daysOverdue > 0).sort((a, b) => b.daysOverdue - a.daysOverdue);
                setUnpaidPurchases(unpaidWithDays);
                setUnpaidInvoices(outstandingWithDays);
                setPendingMemos(pending);
                setApprovedMemos(approved);
                setOverdueMemos(overdueActiveMemos);
                setDraftInvoices(drafts);
                const totalAP = unpaid.reduce((sum, p) => sum + p.amountOwed, 0);
                const totalAR = outstanding.reduce((sum, inv) => sum + (inv.amountDue || inv.total - (inv.amountPaid || 0)), 0);
                setStats({
                    totalAR,
                    totalAP,
                    netCash: totalAR - totalAP
                });
            }
            else {
                // Fetch real data from APIs
                const now = new Date();
                // Fetch memos
                const memosResponse = await apiClient.get('memos/all-v2');
                const memos = memosResponse.ok && memosResponse.data?.memos ? memosResponse.data.memos : [];
                // Fetch invoices
                const invoicesResponse = await apiClient.get('invoices-get');
                const invoices = invoicesResponse.ok && invoicesResponse.data?.invoices ? invoicesResponse.data.invoices : [];
                // Fetch purchases
                const purchasesResponse = await apiClient.get('purchases-list');
                const purchases = purchasesResponse.ok && purchasesResponse.data?.purchases ? purchasesResponse.data.purchases : [];
                // Process purchases
                const unpaid = purchases.filter((p) => p.paymentStatus === 'unpaid' || p.paymentStatus === 'partial');
                const unpaidWithDays = unpaid.map((p) => ({
                    ...p,
                    daysOverdue: p.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(p.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).sort((a, b) => b.daysOverdue - a.daysOverdue);
                // Process invoices
                const outstanding = invoices.filter((inv) => inv.status === 'issued' || inv.status === 'overdue');
                const outstandingWithDays = outstanding.map((inv) => ({
                    ...inv,
                    daysOverdue: inv.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(inv.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).sort((a, b) => b.daysOverdue - a.daysOverdue);
                const drafts = invoices.filter((inv) => inv.status === 'draft');
                // Process memos
                const pending = memos.filter((m) => m.status === 'pending');
                const approved = memos.filter((m) => m.status === 'approved');
                const activeMemos = memos.filter((m) => m.status === 'shipped' || m.status === 'with_client');
                const overdueActiveMemos = activeMemos.map((m) => ({
                    ...m,
                    daysOverdue: m.dueDate ? Math.max(0, Math.floor((now.getTime() - new Date(m.dueDate).getTime()) / (1000 * 60 * 60 * 24))) : 0
                })).filter((m) => m.daysOverdue > 0).sort((a, b) => b.daysOverdue - a.daysOverdue);
                setUnpaidPurchases(unpaidWithDays);
                setUnpaidInvoices(outstandingWithDays);
                setPendingMemos(pending);
                setApprovedMemos(approved);
                setOverdueMemos(overdueActiveMemos);
                setDraftInvoices(drafts);
                const totalAP = unpaid.reduce((sum, p) => sum + (p.amountOwed || 0), 0);
                const totalAR = outstanding.reduce((sum, inv) => sum + (inv.amountDue || inv.total - (inv.amountPaid || 0)), 0);
                setStats({
                    totalAR,
                    totalAP,
                    netCash: totalAR - totalAP
                });
            }
        }
        catch (err) {
            console.error('Failed to fetch dashboard:', err);
            notify.error('Error', 'Failed to load dashboard');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchDashboard();
    }, []);
    const handleRecordPayment = (purchase) => {
        setSelectedPurchase(purchase);
        setShowPaymentModal(true);
    };
    const handleApproveMemo = (memo) => {
        setSelectedMemo(memo);
        setShowMemoModal(true);
    };
    const handlePaymentSuccess = () => {
        fetchDashboard();
    };
    const handleMemoSuccess = () => {
        fetchDashboard();
    };
    const statsData = [
        {
            label: 'Accounts Receivable',
            value: Math.round(stats.totalAR),
            subtitle: unpaidInvoices.length > 0 ? `${unpaidInvoices.length} outstanding invoice${unpaidInvoices.length !== 1 ? 's' : ''}` : 'No outstanding invoices',
            suffix: '$',
            icon: _jsx(FaDollarSign, {}),
            color: '#10b981'
        },
        {
            label: 'Accounts Payable',
            value: Math.round(stats.totalAP),
            subtitle: unpaidPurchases.length > 0 ? `${unpaidPurchases.length} unpaid purchase${unpaidPurchases.length !== 1 ? 's' : ''}` : 'No unpaid purchases',
            suffix: '$',
            icon: _jsx(FaMoneyBillWave, {}),
            color: '#ef4444'
        },
        {
            label: 'Net Cash Flow',
            value: Math.round(stats.netCash),
            subtitle: stats.netCash >= 0 ? 'Positive cash position' : 'Negative cash position',
            alert: stats.netCash < 0,
            suffix: '$',
            icon: _jsx(FaChartLine, {}),
            color: stats.netCash >= 0 ? '#3b82f6' : '#f59e0b'
        },
        {
            label: 'Pending Actions',
            value: pendingMemos.length + approvedMemos.length + overdueMemos.length,
            subtitle: `${pendingMemos.length} pending, ${approvedMemos.length} to ship`,
            alert: overdueMemos.length > 0,
            icon: _jsx(FaCheckCircle, {}),
            color: '#8b5cf6'
        }
    ];
    const hasItems = unpaidPurchases.length > 0 || unpaidInvoices.length > 0 || pendingMemos.length > 0 || approvedMemos.length > 0 || overdueMemos.length > 0 || draftInvoices.length > 0;
    if (loading) {
        return _jsx(Container, { children: _jsx("div", { style: { textAlign: 'center', padding: '3rem' }, children: "Loading dashboard..." }) });
    }
    return (_jsxs(Container, { children: [_jsx(HeaderActions, { children: _jsxs(Button, { "$primary": true, onClick: fetchDashboard, disabled: loading, children: [_jsx(FaSync, { style: { marginRight: '0.5rem' } }), " ", loading ? 'Loading...' : 'Refresh'] }) }), _jsx(DashboardStats, { stats: statsData }), hasItems ? (_jsxs(_Fragment, { children: [unpaidPurchases.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#ef4444", children: [_jsxs(SectionTitle, { children: [_jsx(FaMoneyBillWave, {}), " Pending Supplier Payments"] }), _jsx(SectionBadge, { "$color": "#ef4444", children: unpaidPurchases.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Purchase #" }), _jsx(Th, { children: "Supplier" }), _jsx(Th, { children: "Due Date" }), _jsx(Th, { children: "Overdue" }), _jsx(Th, { children: "Amount Owed" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: unpaidPurchases.slice(0, 5).map((purchase) => (_jsxs(Tr, { children: [_jsx(Td, { style: { fontFamily: 'monospace', fontWeight: 600 }, children: purchase.purchaseNumber }), _jsx(Td, { children: purchase.supplierName }), _jsx(Td, { children: purchase.dueDate ? new Date(purchase.dueDate).toLocaleDateString() : '-' }), _jsx(Td, { children: purchase.daysOverdue > 0 ? (_jsxs(OverdueBadge, { "$days": purchase.daysOverdue, children: [purchase.daysOverdue, " days"] })) : (_jsx("span", { style: { color: 'var(--text-tertiary)', fontSize: '0.75rem' }, children: "Current" })) }), _jsx(Td, { children: _jsxs(PriceCell, { "$warning": purchase.daysOverdue > 30, children: ["$", purchase.amountOwed.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { "$variant": "primary", onClick: () => handleRecordPayment(purchase), children: "Pay" }), _jsx(ActionButton, { onClick: () => onNavigateToTab('purchases'), children: "View" })] }) })] }, purchase.id))) })] }) }), unpaidPurchases.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('purchases'), children: ["View All ", unpaidPurchases.length, " Pending Payments \u2192"] }) }))] })), unpaidInvoices.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#f59e0b", children: [_jsxs(SectionTitle, { children: [_jsx(FaDollarSign, {}), " Outstanding Customer Invoices"] }), _jsx(SectionBadge, { "$color": "#f59e0b", children: unpaidInvoices.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Invoice #" }), _jsx(Th, { children: "Customer" }), _jsx(Th, { children: "Due Date" }), _jsx(Th, { children: "Overdue" }), _jsx(Th, { children: "Amount Due" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: unpaidInvoices.slice(0, 5).map((invoice) => (_jsxs(Tr, { children: [_jsx(Td, { style: { fontFamily: 'monospace', fontWeight: 600 }, children: invoice.invoiceNumber }), _jsx(Td, { children: invoice.customerBusinessName || invoice.customerName }), _jsx(Td, { children: new Date(invoice.dueDate).toLocaleDateString() }), _jsx(Td, { children: invoice.daysOverdue > 0 ? (_jsxs(OverdueBadge, { "$days": invoice.daysOverdue, children: [invoice.daysOverdue, " days"] })) : (_jsx("span", { style: { color: 'var(--text-tertiary)', fontSize: '0.75rem' }, children: "Current" })) }), _jsx(Td, { children: _jsxs(PriceCell, { "$warning": invoice.daysOverdue > 30, children: ["$", (invoice.amountDue || invoice.total).toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { "$variant": "primary", onClick: () => notify.info('Coming Soon', 'Email reminders will be available in a future update.'), children: _jsx(FaEnvelope, {}) }), _jsx(ActionButton, { onClick: () => onNavigateToTab('invoices'), children: "View" })] }) })] }, invoice.id))) })] }) }), unpaidInvoices.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('invoices'), children: ["View All ", unpaidInvoices.length, " Outstanding Invoices \u2192"] }) }))] })), pendingMemos.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#3b82f6", children: [_jsxs(SectionTitle, { children: [_jsx(FaCheckCircle, {}), " Pending Memo Approvals"] }), _jsx(SectionBadge, { "$color": "#3b82f6", children: pendingMemos.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Dealer" }), _jsx(Th, { children: "Client" }), _jsx(Th, { children: "Requested" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: pendingMemos.slice(0, 5).map((memo) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape, " ", memo.diamondDetails.color, " ", memo.diamondDetails.clarity] }) }), _jsx(Td, { children: memo.dealerName }), _jsx(Td, { children: memo.clientName || '-' }), _jsx(Td, { children: new Date(memo.requestedAt).toLocaleDateString() }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", memo.diamondDetails.priceUSD.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { "$variant": "success", onClick: () => handleApproveMemo(memo), title: "Approve", children: _jsx(FaCheck, {}) }), _jsx(ActionButton, { "$variant": "danger", onClick: () => handleApproveMemo(memo), title: "Reject", children: _jsx(FaTimes, {}) }), _jsx(ActionButton, { onClick: () => onNavigateToTab('memos'), children: "View" })] }) })] }, memo.id))) })] }) }), pendingMemos.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('memos'), children: ["View All ", pendingMemos.length, " Pending Memos \u2192"] }) }))] })), approvedMemos.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#8b5cf6", children: [_jsxs(SectionTitle, { children: [_jsx(FaBoxOpen, {}), " Approved Memos - Ready to Ship"] }), _jsx(SectionBadge, { "$color": "#8b5cf6", children: approvedMemos.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Dealer" }), _jsx(Th, { children: "Client" }), _jsx(Th, { children: "Approved" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: approvedMemos.slice(0, 5).map((memo) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape, " ", memo.diamondDetails.color, " ", memo.diamondDetails.clarity] }) }), _jsx(Td, { children: memo.dealerName }), _jsx(Td, { children: memo.clientName || '-' }), _jsx(Td, { children: memo.approvedAt ? new Date(memo.approvedAt).toLocaleDateString() : new Date(memo.requestedAt).toLocaleDateString() }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", memo.diamondDetails.priceUSD.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { "$variant": "primary", onClick: () => handleApproveMemo(memo), title: "Mark as Shipped", children: [_jsx(FaTruck, {}), " Ship"] }), _jsx(ActionButton, { onClick: () => onNavigateToTab('memos'), children: "View" })] }) })] }, memo.id))) })] }) }), approvedMemos.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('memos'), children: ["View All ", approvedMemos.length, " Approved Memos \u2192"] }) }))] })), overdueMemos.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#dc2626", children: [_jsxs(SectionTitle, { children: [_jsx(FaExclamationTriangle, {}), " Overdue Memos - Need Return"] }), _jsx(SectionBadge, { "$color": "#dc2626", children: overdueMemos.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Dealer" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Due Date" }), _jsx(Th, { children: "Overdue" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: overdueMemos.slice(0, 5).map((memo) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape, " ", memo.diamondDetails.color, " ", memo.diamondDetails.clarity] }) }), _jsx(Td, { children: memo.dealerName }), _jsx(Td, { children: _jsx("span", { style: {
                                                                padding: '0.125rem 0.5rem',
                                                                borderRadius: '4px',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600,
                                                                background: memo.status === 'with_client' ? '#06b6d415' : '#8b5cf615',
                                                                color: memo.status === 'with_client' ? '#06b6d4' : '#8b5cf6'
                                                            }, children: memo.status === 'with_client' ? 'With Client' : 'Shipped' }) }), _jsx(Td, { children: memo.dueDate ? new Date(memo.dueDate).toLocaleDateString() : '-' }), _jsx(Td, { children: _jsxs(OverdueBadge, { "$days": memo.daysOverdue, children: [memo.daysOverdue, " days"] }) }), _jsx(Td, { children: _jsxs(PriceCell, { "$warning": memo.daysOverdue > 14, children: ["$", memo.diamondDetails.priceUSD.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { onClick: () => notify.info('Coming Soon', 'Email reminders will be available in a future update.'), children: _jsx(FaEnvelope, {}) }), _jsx(ActionButton, { onClick: () => onNavigateToTab('memos'), children: "View" })] }) })] }, memo.id))) })] }) }), overdueMemos.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('memos'), children: ["View All ", overdueMemos.length, " Overdue Memos \u2192"] }) }))] })), draftInvoices.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#6b7280", children: [_jsxs(SectionTitle, { children: [_jsx(FaFileAlt, {}), " Draft Invoices - Ready to Issue"] }), _jsx(SectionBadge, { "$color": "#6b7280", children: draftInvoices.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Invoice #" }), _jsx(Th, { children: "Customer" }), _jsx(Th, { children: "Created" }), _jsx(Th, { children: "Amount" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: draftInvoices.slice(0, 5).map((invoice) => (_jsxs(Tr, { children: [_jsx(Td, { style: { fontFamily: 'monospace', fontWeight: 600 }, children: invoice.invoiceNumber }), _jsx(Td, { children: invoice.customerBusinessName || invoice.customerName }), _jsx(Td, { children: new Date(invoice.createdAt || invoice.issueDate).toLocaleDateString() }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", invoice.total.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { "$variant": "primary", onClick: () => notify.info('Coming Soon', 'Quick invoice issuing will be available soon.'), children: "Issue" }), _jsx(ActionButton, { onClick: () => onNavigateToTab('invoices'), children: "View" })] }) })] }, invoice.id))) })] }) }), draftInvoices.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('invoices'), children: ["View All ", draftInvoices.length, " Draft Invoices \u2192"] }) }))] }))] })) : (_jsxs(AllCaughtUp, { children: [_jsx(FaCheckCircle, {}), _jsx("h3", { children: "All Caught Up!" }), _jsx("p", { children: "No pending actions required. Great work!" })] })), _jsx(RecordPaymentModal, { isOpen: showPaymentModal, onClose: () => {
                    setShowPaymentModal(false);
                    setSelectedPurchase(null);
                }, purchase: selectedPurchase, onSuccess: handlePaymentSuccess }), _jsx(MemoApprovalModal, { isOpen: showMemoModal, onClose: () => {
                    setShowMemoModal(false);
                    setSelectedMemo(null);
                }, memo: selectedMemo, onSuccess: handleMemoSuccess })] }));
};
export default DashboardTab;
